import "amazon-connect-streams";
import { getLogger } from "@expert/logging";
import { QueryClient } from "@tanstack/react-query";

import { getRootDispatcher } from "@/analytics/rootDispatcher";
import { decryptWithCache, encryptWithCache, evictDataKeys } from "@/encryption";
import { type ChatEventCallbacks, type ChatMessage, type ChatMessagesCache, useChatStore } from "@/sdk/agent/chat";
import { CHAT_RUNTIME_ACTIONS, emitChatRuntimeError } from "@/sdk/agent/chat/emitChatRuntimeError";
import { isUnreadCustomerMessage } from "@/sdk/agent/chat/messageUtils";
import { reportChatEvent } from "@/sdk/agent/chat/reportChatEvent";
import { findTaskById, useSessionStore } from "@/sdk/sessions/session.store";

import type AmazonConnectChatContact from "./AmazonConnectChatContact";

import normalizePartner from "../normalizePartner";
import { getChatMetadataApiClient } from "./api/getChatMetadataApiClient";
import {
    getChatContactConnection,
    removeChatContactConnection,
    setChatContactConnection,
} from "./connection/contactConnectionRegistry";
import { createChatJSConnection } from "./connection/createChatJSConnection";
import { subscribeToConnectionEvents } from "./connection/subscribeToConnectionEvents";
import {
    CHAT_CONTENT_TYPES,
    ChatMetadataResponse,
    type ConnectTranscriptEvents,
    type EncryptionDataFields,
    RelatedChatResponse,
} from "./types";
import { buildChatJSGlobalConfig } from "./utils/chatJSGlobalConfig";
import { contactAttributeEncryptionContext } from "./utils/contactAttributeEncryptionContext";
import { decryptContactAttribute } from "./utils/contactAttributeProcessor";
import { isContactEnded } from "./utils/contactStateUtils";
import { getParticipantIds } from "./utils/getParticipantIds";
import { handleContactEnded } from "./utils/handleContactEnded";
import {
    createConnectSendMessageParams,
    decryptMessageAndHandleErrors,
    processIncomingChatMessage,
} from "./utils/messageProcessor";
import { recordInboundCustomerMessageLatency } from "./utils/recordInboundCustomerMessageLatency";
import { getParticipantJoinTime, getParticipantLeaveTime, isParticipantEvent } from "./utils/transcriptEventsUtils";

const logger = getLogger({ module: "amazonConnectChatSdk" });

/** True only for a real, non-Invalid Date. */
function isUsableDate(value: unknown): value is Date {
    return value instanceof Date && !Number.isNaN(value.getTime());
}

/** Earliest usable message timestamp in a transcript, ignoring missing/invalid dates. */
function earliestMessageTimestamp(messages: ChatMessage[]): Date | undefined {
    let earliest: Date | undefined;
    for (const message of messages) {
        const timestamp = message.dateCreated;
        if (!isUsableDate(timestamp)) continue;
        if (!earliest || timestamp.getTime() < earliest.getTime()) {
            earliest = timestamp;
        }
    }
    return earliest;
}

const analyticsDispatcher = getRootDispatcher().withExtra({ sdkProvider: "AmazonConnectAgentSDK" });

export class ContactEndedError extends Error {
    public readonly contactId: string;
    public readonly action: string;

    constructor(contactId: string, action: string) {
        super(`Cannot ${action}: chat contact ${contactId} has ended`);
        // Restore the prototype chain so `instanceof ContactEndedError` holds even when a build
        // target downlevels `extends Error` (which otherwise resets the prototype to Error).
        Object.setPrototypeOf(this, ContactEndedError.prototype);
        this.name = "ContactEndedError";
        this.contactId = contactId;
        this.action = action;
    }
}

const chatSdkQueryClient = new QueryClient();

/**
 * Every distinct encryption context that may have cached data keys for this contact:
 * attributes: `dataKeyContext ?? contactId` (same as pre–React-Query `syncContactAttributes`);
 * messages: `originalChatId ?? contactId`.
 */
function encryptionContextsForConnectContact(contact: connect.Contact): string[] {
    const contactId = contact.getContactId();
    const attrs = contact.getAttributes();
    const attributeContext = contactAttributeEncryptionContext(attrs, contactId);
    const messageEncryptionContext = attrs.originalChatId?.value ?? contactId;
    return [...new Set([contactId, attributeContext, messageEncryptionContext])];
}

/**
 * Amazon Connect Chat SDK - manages Amazon Connect chat sessions using the ChatJS library.
 *
 * This SDK acts as an abstraction layer over Amazon Connect's ChatJS library (connect.ChatSession),
 * providing a cleaner interface for managing chat connections and message flow.
 *
 * ## Architecture: Clear Separation of Concerns
 *
 * This SDK is designed with a **three-layer architecture** to separate provider-specific logic
 * from universal business logic:
 *
 * 1. **Event Subscription Layer** (`subscribeToConnectionEvents.ts`)
 *    - Provider-specific: Wires up ChatJS event listeners
 *    - Handles raw ChatJS events (onMessage, onTyping, etc.)
 *    - Filters and validates raw event data
 *
 * 2. **Message Processing Layer** (`messageProcessor.ts`)
 *    - Provider-agnostic: Transforms messages to universal format
 *    - Validates message structure (ChatMessagePayload)
 *    - Returns both provider-specific and normalized formats
 *
 * 3. **SDK Orchestration Layer** (this class)
 *    - Universal: Cache management and UI callbacks
 *    - Provider-agnostic business logic
 *    - Easy to extract to base class for multiple providers
 *
 * ## Message Flow:
 *
 * **Real-time Messages (single):**
 * 2. ChatJS fires `onMessage` → `subscribeToConnectionEvents()` receives it
 * 3. Event subscriber validates → calls `messageProcessor.processIncomingChatMessage()`
 * 4. Processor returns normalized message → subscriber calls `handleProcessedMessage()`
 * 5. SDK adds to cache → triggers UI callback (`onMessageAdded`)
 *
 * **Bulk Operations (initial transcript, manual fetch):**
 * 1. Fetch triggered → `syncChatTranscript()` retrieves and caches full transcript
 * 2. SDK adds all to cache → NO individual callbacks
 *
 * ## Key Design Benefits:
 *
 * - **Testability**: Each layer can be tested independently
 * - **Maintainability**: ChatJS-specific code isolated in subscription layer
 * - **Unification Ready**: Universal logic (cache, callbacks) already separated for future base class
 * - **Clear Boundaries**: Easy to identify what's provider-specific vs shared
 *
 * @see ChatMessagePayload for our custom message format (embedded in Amazon Connect's Content field)
 * @see subscribeToConnectionEvents for ChatJS event wiring details
 * @see processIncomingChatMessage for message transformation logic
 * @see https://github.com/amazon-connect/amazon-connect-chatjs
 */
export class AmazonConnectChatSDK {
    private chatEventCallbacks: ChatEventCallbacks;
    private chatMessagesCache: ChatMessagesCache;
    private connectRegion: string;
    private static chatJSLoaded = false;

    private constructor(
        chatEventCallbacks: ChatEventCallbacks,
        chatMessagesCache: ChatMessagesCache,
        connectRegion: string,
    ) {
        this.chatEventCallbacks = chatEventCallbacks;
        this.chatMessagesCache = chatMessagesCache;
        this.connectRegion = connectRegion;
    }

    /**
     * Creates and initializes a new AmazonConnectChatSDK instance.
     * Loads ChatJS library on first instantiation in the global `connect` namespace so it's available everywhere.
     */
    public static async create(
        chatEventCallbacks: ChatEventCallbacks,
        chatMessagesCache: ChatMessagesCache,
        connectRegion: string,
    ): Promise<AmazonConnectChatSDK> {
        // Load and configure ChatJS once (globally) on first SDK creation
        // defensively check to prevent multiple loads in the omni-channel future
        if (!AmazonConnectChatSDK.chatJSLoaded) {
            const { configureChatJS } = await import("./utils/configureChatJS");
            configureChatJS(connectRegion);
            AmazonConnectChatSDK.chatJSLoaded = true;
            logger.info("ChatJS library eagerly loaded and configured for chat channel");
        } else {
            logger.warn(
                "AmazonConnectChatSDK.create() called multiple times - ChatJS already loaded. This may indicate unexpected re-initialization.",
            );
        }

        return new AmazonConnectChatSDK(chatEventCallbacks, chatMessagesCache, connectRegion);
    }

    /**
     * Initializes a connection for a contact, creating it if it doesn't exist,
     * and ensures all necessary event subscriptions are active.
     */
    public async initializeConnection(
        contact: connect.Contact,
        { isRehydratedTask = false }: { isRehydratedTask?: boolean } = {},
    ): Promise<void> {
        const { contactId } = contact;
        const contactLogger = logger.child({ contactId, action: "initializeConnection", isRehydratedTask });

        try {
            const existingConnection = getChatContactConnection(contact);
            if (existingConnection) {
                contactLogger.debug("Connection already exists, skipping");
                return;
            }
            const connection = this.createConnection(contact);

            // ! Subscribe to events *before* "connecting" to prevent race condition for `onConnectionEstablished` event
            // `onConnectionEstablished` is responsible for calling `syncChatTranscript()` to fetch the initial transcript
            subscribeToConnectionEvents(connection, this, contact);
            this.subscribeToContactLifecycleEvents(contact);

            await connection.connect();
            contactLogger.info("ChatJS websocket connected.");

            const attrs = contact.getAttributes();
            await this.chatEventCallbacks?.onChatAdded?.({
                contactId,
                partner: attrs.partner?.value,
                topic: attrs.topic?.value,
                sourceApp: attrs.sourceApp?.value,
                language: attrs.language?.value,
            });

            contactLogger.info("Chat connection initialized");
        } catch (error) {
            contactLogger.error({ error, isRehydratedTask }, `Failed to initialize chat connection`);
            const rawPartner = contact.getAttributes().partner?.value;
            emitChatRuntimeError(
                CHAT_RUNTIME_ACTIONS.connectionInitFailed,
                rawPartner ? (normalizePartner(rawPartner) ?? rawPartner.toLowerCase()) : undefined,
            );
        }
    }

    /**
     * Rehydrates store with chat metadata for a given contact ids. Get the metadata from the backend.
     * Update the store with the neglection and autopilot data.
     * @param contactIds - The contact ids to rehydrate store with chat metadata for.
     */
    public async rehydrateChatMetadata(contactIds: string[]): Promise<void> {
        // Tracks which partner's request was in-flight when the catch fires, for metric attribution.
        let currentPartner: string | undefined;
        try {
            const sessionStore = useSessionStore.getState();
            const contactsByPartner = new Map<string, string[]>();

            for (const contactId of contactIds) {
                const task = sessionStore.getTask(contactId) as AmazonConnectChatContact | undefined;
                if (!task?.partner) {
                    logger.warn({ contactId }, "Skipping rehydration: task not found or missing partner");
                    continue;
                }
                const partnerChatIds = contactsByPartner.get(task.partner) ?? [];
                partnerChatIds.push(contactId);
                contactsByPartner.set(task.partner, partnerChatIds);
            }

            const chatStore = useChatStore.getState();
            const client = getChatMetadataApiClient();

            for (const [partner, chatIds] of contactsByPartner) {
                currentPartner = partner;
                const chatMetadataResponses = await client
                    .post("", {
                        json: {
                            partner,
                            chatIds,
                        },
                    })
                    .json<ChatMetadataResponse[]>();

                for (const chatMetadataResponse of chatMetadataResponses) {
                    const { neglection, autopilot } = chatMetadataResponse;

                    if (neglection && neglection.startTime && !neglection.endTime) {
                        chatStore.updateChatNeglection(chatMetadataResponse.chatId, {
                            warningCount: neglection.warningCount ?? undefined,
                            durationSeconds: neglection.durationSeconds ?? undefined,
                            startTime: neglection.startTime,
                        });
                    } else {
                        chatStore.clearChatNeglection(chatMetadataResponse.chatId);
                    }

                    if (autopilot && autopilot.startTime && !autopilot.endTime) {
                        chatStore.updateChatAutopilot(chatMetadataResponse.chatId, {
                            startTime: autopilot.startTime,
                            durationSeconds: autopilot.durationSeconds,
                            warningCount: autopilot.warningCount ?? undefined,
                        });
                    } else {
                        chatStore.clearChatAutopilot(chatMetadataResponse.chatId);
                    }
                }
            }
        } catch (error) {
            logger.error({ error, contactIds, partner: currentPartner }, "Failed to rehydrate chat metadata");
            emitChatRuntimeError(CHAT_RUNTIME_ACTIONS.metadataApiFailed, currentPartner);
        }
    }

    public async updateChatMetadataSessionId(chatId: string, sessionId: string, partner: string): Promise<void> {
        const contactLogger = logger.child({ sessionId, action: "updateChatMetadataSessionId" });
        try {
            const client = getChatMetadataApiClient();
            await client.patch(`${chatId}`, {
                json: { partner, chatMetadataUpdates: { exwoSessionId: sessionId } },
            });
        } catch (error) {
            contactLogger.error({ error, sessionId }, "Failed to update chat metadata session id");
            emitChatRuntimeError(CHAT_RUNTIME_ACTIONS.metadataApiFailed, partner);
        }
    }

    public async updateChatCloseReason(chatId: string, closeReason: string, partner: string): Promise<void> {
        const contactLogger = logger.child({ chatId, action: "updateChatCloseReason" });
        try {
            const client = getChatMetadataApiClient();
            await client.patch(`${chatId}`, {
                json: { partner, chatMetadataUpdates: { closeReason } },
            });
        } catch (error) {
            contactLogger.error({ error, closeReason }, "Failed to update chat metadata close reason");
            emitChatRuntimeError(CHAT_RUNTIME_ACTIONS.metadataApiFailed, partner);
        }
    }

    async updateChatNeglectionCount(originalChatId: string, chatId: string, partner: string): Promise<void> {
        try {
            const client = getChatMetadataApiClient();
            const relatedChats = await client
                .post(`${originalChatId}`, {
                    json: { partner },
                })
                .json<RelatedChatResponse[]>();
            if (relatedChats.length > 0) {
                const chatStore = useChatStore.getState();
                const neglectionCount = relatedChats.filter(
                    (chatMetadataResponse) =>
                        chatMetadataResponse.closeReason === "Neglection" || !!chatMetadataResponse.originalChatId,
                ).length;
                chatStore.updateChatNeglection(chatId, {
                    neglectionCount: neglectionCount,
                });
            }
        } catch (error) {
            logger.warn({ error, chatId }, "Failed to get related chats");
        }
    }

    /**
     * Updates the ChatJS region for ACGR failover.
     * Reconfigures the SDK instance region (used by new connections), the ChatJS global config,
     * and the ChatJS region override. The override is critical: ChatJS resolves the participant
     * service region as `regionOverride || options.region || globalConfig.region`, so an outdated
     * override (set earlier by Connect Streams) would win over the per-session option we pass in
     * `createChatJSConnection`. Setting it here keeps all three sources in agreement.
     * Existing sessions are unaffected by this change and should clean up via lifecycle events.
     */
    public updateRegion(newRegion: string): void {
        const previousRegion = this.connectRegion;
        this.connectRegion = newRegion;

        connect.ChatSession.setGlobalConfig(buildChatJSGlobalConfig(newRegion));
        connect.ChatSession.setRegionOverride(newRegion);

        logger.info({ previousRegion, newRegion }, "Chat SDK region updated for ACGR failover");
    }

    /**
     * Creates a new ChatJS connection and registers it. Caller should ensure no existing connection exists.
     *
     * Resolves the region from the live ACGR active region first, falling back to the stored
     * `connectRegion`. The `onFailoverCompleted` callback that feeds `updateRegion` can fire
     * before the workspace subscribes (the GR switch completes during the CCP handshake, before
     * the SDK constructor runs), leaving `this.connectRegion` stale at the pre-failover value.
     * `getActiveRegion()` reads the authoritative region that Streams already switched to.
     *
     * Also re-asserts the ChatJS global config and region override before each session creation.
     * Connect Streams' ChatMediaController calls `setGlobalConfig({ region })` for every contact
     * using the region baked into the MediaFactory at CCP init time — which after an ACGR failover
     * is the *original* (pre-failover) region, not the current DR region. Without this re-assertion
     * the ChatJS client cache resolves the wrong participant service endpoint and the expert's
     * `sendMessage` call returns a 403.
     */
    private createConnection(contact: connect.Contact): connect.AgentChatSession {
        const grActiveRegion = connect.globalResiliency.getActiveRegion();
        const region = grActiveRegion ?? this.connectRegion;

        logger.info(
            {
                contactId: contact.getContactId(),
                resolvedRegion: region,
                grActiveRegion: grActiveRegion ?? "none",
                storedRegion: this.connectRegion,
            },
            "Creating ChatJS connection for contact",
        );

        connect.ChatSession.setGlobalConfig(buildChatJSGlobalConfig(region));
        connect.ChatSession.setRegionOverride(region);

        const newConnection = createChatJSConnection(contact, region);
        setChatContactConnection(contact, newConnection);
        return newConnection;
    }

    /**
     * Cleans up everything for a chat contact: removes ChatJS connection from registry,
     * cleans up event listeners, and removes cached state (messages, inputs, spellcheck, etc.).
     *
     * Does not affect the backend connection, which is managed by the Amazon Connect CCP lifecycle.
     */
    public cleanupConnection(contact: connect.Contact): void {
        const { contactId } = contact;
        const connection = getChatContactConnection(contact);

        if (connection) {
            try {
                connection.cleanUpOnParticipantDisconnect();
                logger.debug({ contactId }, "ChatJS event listeners cleaned up");
            } catch (error) {
                logger.warn({ error, contactId }, "Failed to cleanup ChatJS event listeners");
            }
            removeChatContactConnection(contact);
            logger.info({ contactId }, "Chat connection removed from registry");
        } else {
            logger.debug({ contactId }, "Cleanup called but connection already removed (no-op)");
        }

        const encryptionContexts = encryptionContextsForConnectContact(contact);
        evictDataKeys(encryptionContexts);

        this.chatMessagesCache.removeChat(contactId);
    }

    public async sendMessage(
        chatContact: AmazonConnectChatContact,
        message: string,
        metadata: {
            agent: connect.Agent;
            agentId: string;
            author: string;
            senderName: string;
            partner: string;
            sourceApp: string;
            topic: string;
            language: string;
        },
    ): Promise<void> {
        const contact = chatContact.config.contact;
        const contactId = contact.getContactId();

        if (isContactEnded(contact)) {
            throw new ContactEndedError(contactId, "sendMessage");
        }

        const connection = getChatContactConnection(contact);
        if (!connection) {
            throw new Error(`Cannot send message, connection is not available for contact ${contactId}`);
        }

        const encrypt = await encryptWithCache({
            chatId: chatContact.messageEncryptionContext,
            partner: metadata.partner,
        });
        const { encryptedData, encryptedDataKey, IV } = await encrypt(message);

        const encryption: EncryptionDataFields = {
            encrypted: true,
            IV,
            encryptedDataKey,
        };

        const sendParams = createConnectSendMessageParams(encryptedData, metadata, encryption);
        await connection.sendMessage(sendParams);

        logger.debug(
            {
                contactId,
                partner: metadata.partner,
                messageLength: message.length,
                messageEncryptionContext: chatContact.messageEncryptionContext,
            },
            "Expert message sent",
        );
    }

    /**
     * Fetches chat transcript without caching messages.
     * @see `syncChatTranscript()` to fetch and cache messages together.
     */
    public async getMessages(
        contact: connect.Contact,
        options?: { cancelInFlight?: boolean },
    ): Promise<{
        messages: ChatMessage[];
        events: ConnectTranscriptEvents;
    }> {
        const { contactId } = contact;
        const getMessagesLogger = logger.child({ contactId, action: "getMessages" });

        if (options?.cancelInFlight) {
            await chatSdkQueryClient.cancelQueries({
                queryKey: ["chatTranscript", contactId],
            });
            getMessagesLogger.debug("Cancelled in-flight requests before fetching");
        }

        getMessagesLogger.debug("Fetching messages via SDK QueryClient");

        return await chatSdkQueryClient.ensureQueryData({
            queryKey: ["chatTranscript", contactId],
            queryFn: async (_signal) => {
                const connection = getChatContactConnection(contact);
                if (!connection) {
                    getMessagesLogger.warn("No chat connection found");
                    return { messages: [], events: {} };
                }

                try {
                    getMessagesLogger.info("Fetching transcript using pagination");
                    const messages: ChatMessage[] = [];
                    const events: ConnectTranscriptEvents = {};
                    let nextToken: string | undefined;
                    const MAX_PAGES = 30; // max of 3000 raw messages/events
                    const MAX_ITEMS_PER_PAGE = 100;

                    const { agentParticipantId, customerParticipantId } = getParticipantIds(contact);

                    const sessionStore = useSessionStore.getState();
                    const task = sessionStore.getTask(contactId) as AmazonConnectChatContact | undefined;
                    if (!task) {
                        getMessagesLogger.warn({ contactId }, "Task not found for transcript fetch");
                        return { messages: [], events: {} };
                    }

                    for (let pageNumber = 1; pageNumber <= MAX_PAGES; pageNumber++) {
                        const transcript = await connection.getTranscript({
                            scanDirection: "BACKWARD",
                            sortOrder: "ASCENDING",
                            maxResults: MAX_ITEMS_PER_PAGE,
                            nextToken,
                        });

                        if (!transcript.data?.Transcript) {
                            if (pageNumber === 1) {
                                getMessagesLogger.warn("No transcript data found on first page");
                            } else {
                                getMessagesLogger.debug(
                                    { currentPage: pageNumber, messageCount: messages.length },
                                    "No transcript data on subsequent page, stopping pagination",
                                );
                            }
                            break;
                        }

                        // First pass: process events (separate concerns and side effects)
                        for (const item of transcript.data.Transcript) {
                            if (!isParticipantEvent(item.ContentType)) continue;

                            events.agentJoinTime ??= getParticipantJoinTime(item, agentParticipantId);
                            events.customerJoinTime ??= getParticipantJoinTime(item, customerParticipantId);
                            events.agentLeaveTime ??= getParticipantLeaveTime(item, agentParticipantId);
                            events.customerLeaveTime ??= getParticipantLeaveTime(item, customerParticipantId);
                        }

                        // Second pass: process and decrypt messages
                        const getDecrypt = decryptWithCache;
                        for (const item of transcript.data.Transcript) {
                            const processedMessage = processIncomingChatMessage(item);
                            if (!processedMessage) continue;

                            let decryptedBody = processedMessage.normalizedMessage.body;

                            if (item.ContentType === "application/json" && typeof item.Content === "string") {
                                decryptedBody = await decryptMessageAndHandleErrors({
                                    content: item.Content,
                                    getDecrypt,
                                    encryptionContext: task.messageEncryptionContext,
                                    fallbackPartner: task.partner,
                                    messageType: "transcript",
                                    messageId: item.Id,
                                    contactId,
                                });
                            }

                            messages.push({
                                ...processedMessage.normalizedMessage,
                                body: decryptedBody,
                            });
                        }

                        nextToken = transcript.data.NextToken;
                        if (!nextToken) {
                            getMessagesLogger.debug(
                                { currentPage: pageNumber, messageCount: messages.length },
                                "No next token, pagination complete",
                            );
                            break;
                        }

                        if (pageNumber === MAX_PAGES) {
                            getMessagesLogger.warn(
                                { messageCount: messages.length, hasMorePages: !!nextToken },
                                `Reached maximum page limit (${MAX_PAGES}), stopping pagination`,
                            );
                            break;
                        }
                    }

                    getMessagesLogger.info({ messageCount: messages.length }, "Transcript fetched");
                    return { messages, events };
                } catch (error) {
                    getMessagesLogger.warn({ error }, "Failed to fetch or process chat transcript.");
                    return { messages: [], events: {} };
                }
            },
            revalidateIfStale: true,
        });
    }

    /**
     * Fetches transcript and caches messages/events. Recommended method for fetching chat history.
     */
    public async syncChatTranscript(
        contact: connect.Contact,
        options?: { cancelInFlight?: boolean },
    ): Promise<ChatMessage[]> {
        const contactId = contact.getContactId();
        const contactLogger = logger.child({ contactId, action: "syncChatTranscript" });
        const { messages, events } = await this.getMessages(contact, options);

        if (messages.length > 0) {
            this.chatMessagesCache.addChatMessages(contactId, messages);
            contactLogger.debug({ messageCount: messages.length }, "Bulk cached messages without callbacks");
        }

        if (events.agentJoinTime) {
            this.handleAgentJoinTime(contactId, events.agentJoinTime);
            contactLogger.debug(
                { agentJoinTime: events.agentJoinTime.toISOString() },
                "Updated assignedAt time from transcript",
            );
        }

        // Back-fill dateCreated from the transcript (after handleAgentJoinTime so the assignedAt fallback is current).
        this.backfillChatStartTime(contactId, messages, events.customerJoinTime);

        const undeliveredCustomerMessageIds = messages
            .filter(isUnreadCustomerMessage)
            .map((msg: ChatMessage) => msg.id);

        if (undeliveredCustomerMessageIds.length > 0) {
            this.setMessagesDelivered(contact, undeliveredCustomerMessageIds);
        }

        return messages;
    }

    public async sendTypingIndicator(contact: connect.Contact): Promise<void> {
        const { contactId } = contact;
        if (isContactEnded(contact)) return;

        const connection = getChatContactConnection(contact);
        if (!connection) {
            logger.warn({ contactId, action: "sendTypingIndicator" }, "No chat connection found");
            return;
        }

        try {
            await connection.sendEvent({
                contentType: CHAT_CONTENT_TYPES.TYPING,
            });
        } catch (error) {
            logger.warn({ error, contactId, action: "sendTypingIndicator" }, "Failed to send typing indicator");
        }
    }

    public handleCustomerTyping(_contactId: string): void {
        // TODO: Implement typing indicator state management with timeout tracking or metadata service
    }

    public setMessagesDelivered(contact: connect.Contact, messageIds: string[]): void {
        if (isContactEnded(contact)) return;

        const connection = getChatContactConnection(contact);
        if (!connection || messageIds.length === 0) return;

        const { contactId } = contact;
        messageIds.forEach((messageId) => {
            // chatJs handles throttling, batching, deduplication, and logging events internally.
            // Swallow the vendor rejection (e.g. "Cannot set properties of undefined") that can
            // surface when the session ends between the guard above and this fire-and-forget send.
            void connection
                .sendEvent({
                    contentType: CHAT_CONTENT_TYPES.MESSAGE_DELIVERED,
                    content: JSON.stringify({ messageId }),
                })
                .catch((error) =>
                    logger.debug(
                        { error, contactId, messageId, action: "setMessagesDelivered" },
                        "Ignored delivered-receipt send failure on ended chat",
                    ),
                );
        });
    }

    public async setAllMessagesRead(contact: connect.Contact): Promise<void> {
        const { contactId } = contact;
        if (isContactEnded(contact)) return;

        const connection = getChatContactConnection(contact);
        if (!connection) return;

        const unreadMessageIds: string[] = [];
        // Collect IDs and optimistically update cache ( we don't get read events for experts)
        this.chatMessagesCache.updateChatMessages(
            isUnreadCustomerMessage,
            (message) => {
                unreadMessageIds.push(message.id);
                return { ...message, receiptStatus: "read" };
            },
            contactId,
        );

        if (unreadMessageIds.length === 0) return;

        unreadMessageIds.forEach((messageId: string) => {
            // chatJs handles throttling, batching, deduplication, and logging events internally.
            // Swallow the vendor rejection (e.g. "Cannot set properties of undefined") that can
            // surface when the session ends between the guard above and this fire-and-forget send.
            void connection
                .sendEvent({
                    contentType: CHAT_CONTENT_TYPES.MESSAGE_READ,
                    content: JSON.stringify({ messageId }),
                })
                .catch((error) =>
                    logger.debug(
                        { error, contactId, messageId, action: "setAllMessagesRead" },
                        "Ignored read-receipt send failure on ended chat",
                    ),
                );
        });
    }

    public handleCustomerReadReceipt(contactId: string, messageId: string): void {
        try {
            this.chatMessagesCache.updateChatMessages(
                (message) => message.id === messageId && message.senderType !== "customer",
                (message) => ({ ...message, receiptStatus: "read" }),
                contactId,
            );
        } catch (error) {
            logger.warn({ error, contactId, messageId }, "Failed to handle customer read receipt");
        }
    }

    /**
     * Processes and decrypts an incoming message from Amazon Connect.
     */
    public async handleIncomingMessage(contactId: string, data: connect.ChatEventData): Promise<void> {
        try {
            const processedMessage = processIncomingChatMessage(data);
            if (!processedMessage) return;

            const sessionStore = useSessionStore.getState();
            const task = sessionStore.getTask(contactId) as AmazonConnectChatContact | undefined;
            if (!task) {
                logger.warn({ contactId, messageId: data.Id }, "Cannot process message: task not found");
                return;
            }

            let decryptedBody = processedMessage.normalizedMessage.body;

            if (data.ContentType === "application/json" && typeof data.Content === "string") {
                decryptedBody = await decryptMessageAndHandleErrors({
                    content: data.Content,
                    getDecrypt: decryptWithCache,
                    encryptionContext: task.messageEncryptionContext,
                    fallbackPartner: task.partner,
                    messageType: "message",
                    messageId: data.Id,
                    contactId,
                });
            }

            const finalMessage: ChatMessage = {
                ...processedMessage.normalizedMessage,
                body: decryptedBody,
            };

            // Emit customer→expert latency on first real-time delivery of a customer message.
            // Bulk transcript sync bypasses this method; recordInboundCustomerMessageLatency
            // itself gates on senderType / receiptStatus / serverReceivedTimestamp presence,
            // and additionally rejects backfilled history messages (serverReceivedTimestamp
            // predating task.assignedAt) so this call is safe to make unconditionally.
            recordInboundCustomerMessageLatency(finalMessage, {
                chatAssignedAt: task.assignedAt,
            });

            this.handleProcessedMessage(contactId, finalMessage, task.partner);
        } catch (error) {
            logger.error({ error, contactId, messageId: data.Id }, "Failed to handle incoming message");
        }
    }

    /**
     * Handles a fully-processed message by adding it to cache and notifying callbacks.
     *
     * This method is intentionally provider-agnostic and contains only universal business logic
     * (cache updates and UI callbacks).
     *
     * @todo This logic could be extracted to a base class shared by multiple providers.
     *
     * @param contactId The contact/chat identifier
     * @param message The fully-normalized message in universal format
     * @param partner Partner label used only for `ChatRuntimeError` attribution if this throws.
     */
    public handleProcessedMessage(contactId: string, message: ChatMessage, partner?: string): void {
        const contactLogger = logger.child({ contactId, messageId: message.id });

        try {
            this.chatMessagesCache.addChatMessages(contactId, [message]);
            contactLogger.debug(
                {
                    messageId: message.id,
                    senderType: message.senderType,
                },
                "Message added to cache, notifying callback",
            );

            void this.chatEventCallbacks.onMessageAdded(message);
        } catch (error) {
            contactLogger.error({ error }, "Failed to process incoming message");
            emitChatRuntimeError(CHAT_RUNTIME_ACTIONS.messageProcessFailed, partner);
        }
    }

    /**
     * Updates the task's assignedAt time with the accurate assignment time from the transcript
     */
    public handleAgentJoinTime(contactId: string, agentJoinTime: Date): void {
        const contactLogger = logger.child({ contactId, action: "handleAgentJoinTime" });

        try {
            const sessionStore = useSessionStore.getState();
            const task = sessionStore.getTask(contactId);

            if (!task) {
                contactLogger.warn("Task not found in session store, cannot update assignedAt time");
                return;
            }

            // Update the assignedAt time in the session store
            sessionStore.setTaskKey<AmazonConnectChatContact, "assignedAt">(contactId, "assignedAt", agentJoinTime);

            contactLogger.debug(
                {
                    oldAssignedAt: task.assignedAt?.toISOString(),
                    newAssignedAt: agentJoinTime.toISOString(),
                },
                "Updated task assignedAt time from transcript",
            );
        } catch (error) {
            contactLogger.warn({ error }, "Failed to update assignedAt time from transcript");
        }
    }

    /**
     * Back-fills the task's `dateCreated` (chat start time) when missing. Connect chats have no
     * queueTimestamp and Streams exposes no creation timestamp, so this uses the best available proxy:
     * the customer's PARTICIPANT_JOINED time, then the earliest transcript message, then `assignedAt`.
     * Never overwrites an existing value.
     */
    public backfillChatStartTime(contactId: string, messages: ChatMessage[], customerJoinTime?: Date): void {
        const contactLogger = logger.child({ contactId, action: "backfillChatStartTime" });

        try {
            const sessionStore = useSessionStore.getState();
            const task = sessionStore.getTask(contactId) as AmazonConnectChatContact | undefined;

            if (!task) {
                contactLogger.warn("Task not found in session store, cannot back-fill dateCreated");
                return;
            }

            // Never overwrite a real value; only back-fill when dateCreated is genuinely absent.
            if (isUsableDate(task.dateCreated)) {
                return;
            }

            const chatStartTime =
                (isUsableDate(customerJoinTime) ? customerJoinTime : undefined) ??
                earliestMessageTimestamp(messages) ??
                task.assignedAt;
            if (!isUsableDate(chatStartTime)) {
                contactLogger.debug("No transcript timestamp or assignedAt available to back-fill dateCreated");
                return;
            }

            sessionStore.setTaskKey<AmazonConnectChatContact, "dateCreated">(contactId, "dateCreated", chatStartTime);

            contactLogger.debug(
                { dateCreated: chatStartTime.toISOString() },
                "Back-filled task dateCreated from transcript",
            );
        } catch (error) {
            contactLogger.warn({ error }, "Failed to back-fill dateCreated from transcript");
        }
    }

    /**
     * Decrypts and syncs contact attributes (mdn, firstName, problemStatement).
     *
     * Contact attributes use a different encryption context than messages.
     * The `dataKeyContext` contact attribute is read and used as the
     * decryption context, falling back to `contactId` if not present. (see `contactAttributeEncryptionContext`)
     *
     * No-op if attributes are already decrypted (not loading and no error).
     */
    public async syncContactAttributes(contact: connect.Contact): Promise<void> {
        const contactId = contact.getContactId();
        const contactAttributes = contact.getAttributes();

        const sessionStore = useSessionStore.getState();
        const task = sessionStore.getTask(contactId) as AmazonConnectChatContact | undefined;
        if (!task) {
            logger.warn({ contactId }, "Task not found for syncContactAttributes");
            return;
        }

        // Skip if already decrypted (not loading and no error)
        const alreadyDecrypted =
            !task.mdn.loading && !task.mdn.error && !task.customerName.loading && !task.customerName.error;
        if (alreadyDecrypted) {
            logger.debug({ contactId }, "Contact attributes already decrypted, skipping");
            return;
        }

        const partnerValue = contactAttributes.partner?.value ?? task.partner;
        if (!partnerValue) {
            // Attribute to the contact being synced (contactId === sessionId for Connect chat), else
            // the dispatcher back-fills SessionId from the globally-selected chat.
            reportChatEvent(
                {
                    dispatcher: analyticsDispatcher.withIdentities(contactId ? { SessionId: contactId } : {}),
                    logger,
                },
                "ChatDecryptionFailed",
                { messageType: "contactAttribute", errorReason: "missing_partner" },
                {
                    log: {
                        level: "error",
                        message: "Cannot decrypt contact attributes: missing partner",
                        fields: { contactId },
                    },
                    runtimeError: { action: CHAT_RUNTIME_ACTIONS.attributeDecryptFailed },
                },
            );
            return;
        }

        const partner = normalizePartner(partnerValue) ?? partnerValue.toLowerCase();
        const encryptionContext = contactAttributeEncryptionContext(contactAttributes, contactId);
        const getDecrypt = decryptWithCache;

        // Helper to safely set task key only if task still exists (prevents race condition errors)
        const safeSetTaskKey = <K extends "mdn" | "customerName" | "problemStatement">(
            key: K,
            value: AmazonConnectChatContact[K],
        ) => {
            const task = sessionStore.getTask(contactId);
            if (!task) {
                logger.debug({ contactId, key }, "Task no longer exists, skipping setTaskKey");
                return;
            }
            sessionStore.setTaskKey<AmazonConnectChatContact, K>(contactId, key, value);
        };

        // Batch set loading state for all three attributes in a single state update (reduces re-renders).
        // Resolve from the immer draft via findTaskById, NOT state.getTask(): getTask() reads the
        // committed, auto-frozen state, so writing task.mdn below would throw.
        sessionStore.mutateStore((state) => {
            const task = findTaskById(state, contactId) as AmazonConnectChatContact | undefined;
            if (!task) {
                logger.debug({ contactId }, "Task no longer exists, skipping batch setTaskKey");
                return;
            }
            task.mdn = { loading: true, value: undefined, error: undefined };
            task.customerName = { loading: true, value: undefined, error: undefined };
            task.problemStatement = { loading: true, value: undefined, error: undefined };
        });

        // Returns true if this attribute failed to decrypt, so the caller can emit a single
        // ChatRuntimeError per sync rather than one per failed attribute.
        const decryptAndSetAttribute = async (
            attrKey: "mdn" | "firstName" | "problemStatement",
            taskKey: "mdn" | "customerName" | "problemStatement",
        ): Promise<boolean> => {
            const attribute = contactAttributes[attrKey];
            if (!attribute?.value) {
                safeSetTaskKey(taskKey, {
                    loading: false,
                    value: undefined,
                    error: undefined,
                });
                return false;
            }

            const result = await decryptContactAttribute(
                getDecrypt,
                attribute.value,
                encryptionContext,
                partner,
                attrKey,
                contactId,
            );

            if (result.error) {
                reportChatEvent(
                    {
                        dispatcher: analyticsDispatcher
                            .withIdentities(contactId ? { SessionId: contactId } : {})
                            .withExtra({ partner }),
                        logger,
                    },
                    "ChatDecryptionFailed",
                    { messageType: "contactAttribute", errorReason: "decryption_error" },
                );
            }

            safeSetTaskKey(taskKey, {
                loading: false,
                value: result.value,
                error: result.error,
            });

            return result.error ?? false;
        };

        // Sequential decryption: first fetches key from API, subsequent use cache
        void (async () => {
            const anyFailed = [
                await decryptAndSetAttribute("mdn", "mdn"),
                await decryptAndSetAttribute("firstName", "customerName"),
                await decryptAndSetAttribute("problemStatement", "problemStatement"),
            ].some(Boolean);

            if (anyFailed) {
                emitChatRuntimeError(CHAT_RUNTIME_ACTIONS.attributeDecryptFailed, partner);
            }
        })();
    }

    /**
     * Subscribes to Amazon Connect contact lifecycle events to detect when chat ends.
     *
     * Note: AmazonConnectAgentSDK also subscribes to contact.onEnded() for task state
     * transitions (wrapping, destroy). Both handlers fire - Connect Streams callbacks
     * are additive, not overwriting. Order of execution is not guaranteed.
     *
     * Prefer AmazonConnectAgentSDK for task state management. This handler is specifically
     * for chat-specific cleanup (UI callbacks, metadata fetching).
     */
    private subscribeToContactLifecycleEvents(contact: connect.Contact): void {
        const { contactId } = contact;
        const contactLogger = logger.child({ contactId, action: "contactLifecycleEvents" });

        contact.onEnded(() => {
            contactLogger.debug("Contact ended event received");
            const attrs = contact.getAttributes();
            void this.chatEventCallbacks?.onChatClosed?.({
                contactId,
                partner: attrs.partner?.value,
                topic: attrs.topic?.value,
                sourceApp: attrs.sourceApp?.value,
                language: attrs.language?.value,
            });
            void handleContactEnded(contact, logger);
        });
    }
}
